import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { api, erroMsg } from "../../lib/api";
import { Loader2, Plus, Trash2 } from "lucide-react";

export default function AdminCondicoes() {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState({ nome: "", descricao: "" });
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get("/admin/payment-terms");
      setItens(r.data.itens);
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const criar = async () => {
    if (!form.nome.trim()) {
      toast.error("Informe o nome da condição (ex.: 30DDL).");
      return;
    }
    setSalvando(true);
    try {
      await api.post("/admin/payment-terms", {
        nome: form.nome,
        descricao: form.descricao,
        ativo: true,
        ordem: itens.length,
      });
      toast.success("Condição de pagamento criada");
      setForm({ nome: "", descricao: "" });
      carregar();
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setSalvando(false);
    }
  };

  const salvarCampo = async (id, patch) => {
    try {
      await api.put(`/admin/payment-terms/${id}`, patch);
      carregar();
    } catch (e) {
      toast.error(erroMsg(e));
      carregar();
    }
  };

  const excluir = async (id) => {
    try {
      await api.delete(`/admin/payment-terms/${id}`);
      toast.success("Condição excluída");
      carregar();
    } catch (e) {
      toast.error(erroMsg(e));
    }
  };

  return (
    <AdminShell
      titulo="Condições de pagamento"
      descricao="As condições ativas aparecem para o representante escolher ao montar o orçamento e saem impressas na proposta em PDF."
    >
      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 font-display text-sm font-semibold text-slate-900">Nova condição</div>
        <div className="grid gap-3 sm:grid-cols-[180px_minmax(0,1fr)_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Ex.: 30DDL"
              className="h-11"
              data-testid="admin-condicao-nome"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Descrição (opcional)</Label>
            <Input
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              placeholder="Ex.: 30 dias da data da liberação da mercadoria"
              className="h-11"
              data-testid="admin-condicao-descricao"
            />
          </div>
          <Button className="h-11" onClick={criar} disabled={salvando} data-testid="admin-condicao-criar">
            {salvando ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1.5 h-4 w-4" />
            )}
            Adicionar
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table data-testid="admin-condicoes-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Nome</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-[110px]">Ativa</TableHead>
              <TableHead className="w-[90px] text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" />
                </TableCell>
              </TableRow>
            ) : itens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-slate-500">
                  Nenhuma condição cadastrada.
                </TableCell>
              </TableRow>
            ) : (
              itens.map((c) => (
                <TableRow key={c.id} data-testid={`admin-condicao-row-${c.nome}`}>
                  <TableCell>
                    <Input
                      defaultValue={c.nome}
                      onBlur={(e) =>
                        e.target.value !== c.nome && salvarCampo(c.id, { nome: e.target.value })
                      }
                      className="h-9 font-semibold"
                      data-testid={`admin-condicao-nome-${c.nome}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      defaultValue={c.descricao || ""}
                      onBlur={(e) =>
                        e.target.value !== (c.descricao || "") &&
                        salvarCampo(c.id, { descricao: e.target.value })
                      }
                      className="h-9"
                      data-testid={`admin-condicao-desc-${c.nome}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={c.ativo}
                      onCheckedChange={(v) => salvarCampo(c.id, { ativo: v })}
                      data-testid={`admin-condicao-ativo-${c.nome}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-destructive"
                      onClick={() => excluir(c.id)}
                      data-testid={`admin-condicao-excluir-${c.nome}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <p className="mt-3 text-[12px] text-slate-500">
        A <b>validade da proposta é fixa em 10 dias úteis</b> e é calculada automaticamente na
        emissão do PDF. O <b>prazo de entrega</b> é informado pelo representante em cada orçamento.
      </p>
    </AdminShell>
  );
}
